import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import type { SnapshotPhoto } from "../../shared/types";
import { photoUrl } from "../lib/api";

interface Props {
  photos: SnapshotPhoto[];
  initialIndex: number;
  onClose: () => void;
}

export function PhotoLightbox({ photos, initialIndex, onClose }: Props) {
  const [index, setIndex] = useState(initialIndex);
  const count = photos.length;
  const hasMultiple = count > 1;

  const goPrev = useCallback(() => {
    setIndex((i) => (i - 1 + count) % count);
  }, [count]);
  const goNext = useCallback(() => {
    setIndex((i) => (i + 1) % count);
  }, [count]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // StoreSheet も document に Escape リスナーを張っているため、
      // capture フェーズで先に受けて immediate propagation を止め、
      // 背後のシートが同時に閉じないようにする。
      if (e.key === "Escape") {
        e.stopImmediatePropagation();
        onClose();
      } else if (e.key === "ArrowLeft" && hasMultiple) {
        e.stopImmediatePropagation();
        goPrev();
      } else if (e.key === "ArrowRight" && hasMultiple) {
        e.stopImmediatePropagation();
        goNext();
      }
    };
    document.addEventListener("keydown", handler, { capture: true });
    // サムネイル裏のページがスクロールしないようロック
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handler, { capture: true });
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, goPrev, goNext, hasMultiple]);

  const current = photos[index];
  if (!current) return null;
  // サムネイルと完全同一 URL。HTTP キャッシュがヒットするので
  // 写真切替で R2 への追加リクエストは発生しない。
  const src = photoUrl(current.key);

  // StoreSheet が backdrop-filter / transform を持つため、その内部に
  // 描画すると `fixed inset-0` がビューポートではなくシートを基準に
  // 解決されてしまう。document.body 直下にポータルして CSS の
  // containing block 制約から脱出する。
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="写真を拡大表示"
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm"
    >
      <img
        key={current.key}
        src={src}
        alt=""
        onClick={(e) => e.stopPropagation()}
        className="max-h-[95dvh] max-w-[95vw] object-contain"
      />

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        aria-label="閉じる"
        className="absolute right-3 top-3 rounded-full bg-slate-900/80 p-2 text-slate-200 transition hover:bg-slate-800 hover:text-white sm:right-4 sm:top-4"
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          aria-hidden
        >
          <path d="M6 6l12 12M18 6l-12 12" />
        </svg>
      </button>

      {hasMultiple && (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              goPrev();
            }}
            aria-label="前の写真"
            className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-slate-900/80 p-3 text-slate-200 transition hover:bg-slate-800 hover:text-white sm:left-4"
          >
            <svg
              width="26"
              height="26"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              goNext();
            }}
            aria-label="次の写真"
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-slate-900/80 p-3 text-slate-200 transition hover:bg-slate-800 hover:text-white sm:right-4"
          >
            <svg
              width="26"
              height="26"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M9 6l6 6-6 6" />
            </svg>
          </button>

          <div
            className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-slate-900/80 px-3 py-1 text-xs font-medium text-slate-200"
            aria-live="polite"
          >
            {index + 1} / {count}
          </div>
        </>
      )}
    </div>,
    document.body,
  );
}
